import React, { useState, useEffect } from "react";
import { X, Plus, Pencil, Trash2, Globe, GripHorizontal } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { SOCIAL_ICONS } from "data/staticData";
import { editProfile } from 'services/creatorService'; // Import the API function

export default function SocialLinksModalProfile({ open, onClose, links, onChange }) {
  const [screen, setScreen] = useState("list"); // list | add | edit
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");

  const [editId, setEditId] = useState(null);

  // Reset modal
  useEffect(() => {
    if (!open) {
      setScreen("list");
      setSelectedPlatform(null);
      setLinkUrl("");
      setLinkName("");
      setEditId(null);
    }
  }, [open]);

  const startAdd = () => {
    setScreen("add");
    setSelectedPlatform(null);
    setLinkUrl("");
    setLinkName("");
  };

  const startEdit = (item) => {
    setScreen("edit");
    setEditId(item.id);
    setSelectedPlatform(item.platform);
    setLinkUrl(item.url);
    setLinkName(item.name);
  };

  const saveLink = async () => {
  if (!selectedPlatform || !linkUrl.trim()) return;

  let updated = [...links];

  if (screen === "edit") {
    updated = updated.map((i) =>
      i.id === editId
        ? {
            ...i,
            platform: selectedPlatform,
            url: linkUrl.trim(),
          }
        : i
    );
  } else {
    updated.push({
      id: Date.now(),
      platform: selectedPlatform,
      url: linkUrl.trim(),
      name:
        selectedPlatform !== "custom"
          ? SOCIAL_ICONS.find((p) => p.id === selectedPlatform)?.label
          : linkName,
    });
  }

  try {
    const payload = {
      social_media_links: updated.map((item) => ({
        platform: item.platform,
        url: item.url,
      })),
    };

    console.log("FINAL STRINGIFIED PAYLOAD 👉", JSON.stringify(payload));

    const result = await editProfile(JSON.stringify(payload));

    if (result?.success) {
      onChange(updated);
      setScreen("list");
    }
  } catch (error) {
    console.error("Failed to save social link:", error);
  }
};


  const deleteLink = (id) => {
    onChange(links.filter((l) => l.id !== id));
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
         ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center duration-200
         ${open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"} mx-10 lg:mx-0`}
      >
        <div className="bg-[#FDFAF7] w-[500px] rounded-2xl shadow-xl p-6 relative">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Add Social Links</h2>
            <button onClick={onClose}>
              <X />
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-5">
            Add links that showcase your work, recognition, personality and more!
          </p>

          {/* Platform Selector */}
          <div className="flex gap-1.5 lg:gap-3 mb-5">
            {SOCIAL_ICONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedPlatform(s.id)}
                className={`flex flex-col items-center gap-1 border rounded-xl p-1 lg:p-3
                  ${selectedPlatform === s.id ? "bg-white" : "bg-[#FDFAF7]"}`}
              >
                {s?.icon ? (
                  <s.icon className="w-5 h-5" />
                ) : (
                  <img src={s.src} alt={s.label} className="w-5 h-5" />
                )}
              </button>
            ))}
          </div>

          {/* ADD / EDIT Screen */}
          {(screen === "add" || screen === "edit") && (
            <div className="space-y-3">
              <Input
                placeholder="https://your-link.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="h-11"
              />

              <Input
                placeholder="Name the link..."
                value={
                  selectedPlatform !== "custom"
                    ? SOCIAL_ICONS.find((p) => p.id === selectedPlatform)?.label || ""
                    : linkName
                }
                disabled={selectedPlatform !== "custom"}
                onChange={(e) => {
                  if (selectedPlatform === "custom") setLinkName(e.target.value);
                }}
                className="h-11"
              />

              <div className="flex justify-end mt-6 gap-3">
                <Button
                  variant="outline"
                  onClick={() => setScreen("list")}
                  className="rounded-full px-6"
                >
                  Back
                </Button>

                <Button onClick={saveLink} className="rounded-full px-6">
                  Save Link
                </Button>
              </div>
            </div>
          )}

          {/* LIST Screen */}
          {screen === "list" && (
            <>
              <p className="text-sm text-gray-500 mb-3">{links.length}/7</p>

              <div className="space-y-3 max-h-[260px] overflow-auto pr-2">
                {links.map((item) => {
                  const platform = SOCIAL_ICONS.find((i) => i.id === item.platform);

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between bg-white p-3 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <GripHorizontal size={16} className="text-gray-400" />

                        {/* Icon logic */}
                        {platform?.src ? (
                          <img src={platform.src} className="w-5 h-5" alt={platform.label} />
                        ) : platform?.icon ? (
                          <platform.icon className="w-5 h-5" />
                        ) : (
                          <Globe className="w-5 h-5" />
                        )}

                        <span>{item.name}</span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(item)}
                          className="p-2 rounded-lg bg-[#FDFAF7]"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => deleteLink(item.id)}
                          className="p-2 rounded-lg bg-[#FDFAF7]"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Link Button */}
              <button
                className="flex items-center gap-2 mt-4 text-gray-700"
                onClick={startAdd}
              >
                <div className="w-8 h-8 rounded-full border flex items-center justify-center">
                  <Plus size={16} />
                </div>
                Add another link
              </button>

              {/* Footer */}
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={onClose} className="rounded-full px-6">
                  Close
                </Button>
                <Button
                  onClick={() => {
                    onChange(links);
                    onClose();
                  }}
                  className="rounded-full px-6"
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
